import { useEffect, useState } from 'react'

import { type BannerConfig } from '~components/layout/bannerConfig'

interface UseBannersReturn {
  activeBanners: BannerConfig[]
  currentBannerIndex: number
  currentBanner: BannerConfig | undefined
  handleDismissBanner: () => void
  goToNextBanner: () => void
  goToPrevBanner: () => void
}

const useBanners = (banners: BannerConfig[]): UseBannersReturn => {
  const [activeBanners, setActiveBanners] = useState<BannerConfig[]>([])
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0)

  useEffect(() => {
    const now = new Date()

    const activeBannersList = banners.filter((banner) => {
      const storageKey = `banner_${banner.id}`
      const dismissedTimestamp = localStorage.getItem(storageKey)

      if (dismissedTimestamp) {
        if (banner.reappearAfterMonths) {
          const dismissedDate = new Date(parseInt(dismissedTimestamp))
          const timeSinceDismissal = now.getTime() - dismissedDate.getTime()
          const reappearMs = banner.reappearAfterMonths * 30 * 24 * 60 * 60 * 1000

          if (timeSinceDismissal < reappearMs) {
            return false
          }
          localStorage.removeItem(storageKey)
        } else {
          return false
        }
      }

      if (banner.startDate) {
        const startDate = new Date(banner.startDate)
        if (now < startDate) {
          return false
        }
      }

      if (banner.endDate) {
        const endDate = new Date(banner.endDate)
        if (now > endDate) {
          return false
        }
      }

      if (banner.showLastDaysOfMonth) {
        const currentDay = now.getDate()
        const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
        const daysUntilEndOfMonth = lastDayOfMonth - currentDay

        if (daysUntilEndOfMonth >= banner.showLastDaysOfMonth) {
          return false
        }
      }

      if (banner.showFirstDaysOfMonth) {
        const currentDay = now.getDate()
        if (currentDay > banner.showFirstDaysOfMonth) {
          return false
        }
      }

      return true
    })

    setActiveBanners(activeBannersList)
  }, [banners])

  const handleDismissBanner = (): void => {
    if (activeBanners.length === 0) return

    const currentBanner = activeBanners[currentBannerIndex]
    const storageKey = `banner_${currentBanner.id}`
    const now = new Date().getTime().toString()
    localStorage.setItem(storageKey, now)

    const updatedBanners = activeBanners.filter((_, index) => index !== currentBannerIndex)
    setActiveBanners(updatedBanners)

    if (currentBannerIndex >= updatedBanners.length) {
      setCurrentBannerIndex(Math.max(0, updatedBanners.length - 1))
    }
  }

  const currentBanner = activeBanners[currentBannerIndex]

  const goToNextBanner = (): void => {
    if (activeBanners.length <= 1) return
    setCurrentBannerIndex((currentBannerIndex + 1) % activeBanners.length)
  }

  const goToPrevBanner = (): void => {
    if (activeBanners.length <= 1) return
    setCurrentBannerIndex((currentBannerIndex - 1 + activeBanners.length) % activeBanners.length)
  }

  return {
    activeBanners,
    currentBannerIndex,
    currentBanner,
    handleDismissBanner,
    goToNextBanner,
    goToPrevBanner,
  }
}

export default useBanners
